import type { TicketJournalEvent } from "@zplanem/responses"
import type React from "react"
import { getTranslations } from "../../../i18n"
import { useLocale } from "../../../locale-context"
import { JournalRowShell } from "../journal-row-shell"
import { interpolate, resolveActor } from "../resolve-actor"

const TicketIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<rect x="2.5" y="2" width="11" height="12" rx="1.5" />
		<path d="m5.5 8.5 1.8 1.8 3.2-3.6" />
	</svg>
)

/**
 * Ticket journal rows (ZPL-287). Statuses render localized via
 * `t.tickets.status`; unknown status strings (forward-compat) fall back to
 * the raw value. The API already visibility-filters these events per viewer.
 */
export function TicketEventRow({ event }: { event: TicketJournalEvent }) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const actor = resolveActor(event.actor, t)

	const statusLabel = (s: string | undefined): string => {
		if (!s) return ""
		const known = t.tickets.status as Record<string, string>
		return known[s] ?? s
	}

	const params: Record<string, string> = { actor, title: event.payload.title }
	let copy: { label: string; aria: string }
	switch (event.type) {
		case "ticket.created":
			copy =
				event.payload.kind === "defect"
					? t.journal.events.ticketCreatedDefect
					: t.journal.events.ticketCreated
			break
		case "ticket.status_changed":
			copy = t.journal.events.ticketStatusChanged
			params.from = statusLabel(event.payload.from)
			params.to = statusLabel(event.payload.to)
			break
		case "ticket.assigned":
			if (event.payload.assignee?.userId) {
				copy = t.journal.events.ticketAssigned
				params.assignee =
					event.payload.assignee.displayName ?? t.journal.deletedUser
			} else {
				copy = t.journal.events.ticketUnassigned
			}
			break
	}

	return (
		<JournalRowShell
			icon={<TicketIcon />}
			headline={interpolate(copy.label, params)}
			actor={actor}
			iso={event.createdAt}
			eventType={event.type}
			aria={interpolate(copy.aria, params)}
		/>
	)
}
